import React, { useEffect, useState } from "react";
import { apiFetch } from "../../services/api";
import LoadingButton from "../../components/common/LoadingButton";
import V2AuthLayout from "../components/V2AuthLayout";
import { useV2Theme } from "../theme/ThemeProvider";

const inputClass =
  "mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500/25";

const inputDisabledClass =
  "mt-1 block w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600 shadow-sm cursor-not-allowed";

function parseResetError(response, responseText) {
  if (!responseText?.trim()) {
    return response.statusText
      ? `Failed to reset password: ${response.statusText}`
      : "Failed to reset password";
  }
  try {
    const errorData = JSON.parse(responseText);
    if (errorData.error) return String(errorData.error);
    if (errorData.detail?.message) return String(errorData.detail.message);
    if (typeof errorData.detail === "string") return errorData.detail;
    if (errorData.message) return String(errorData.message);
  } catch {
    if (responseText.length < 500) return responseText;
  }
  return "Failed to reset password";
}

export default function V2ResetPasswordPage({
  email,
  token,
  onSuccess,
  isInvitation = false,
}) {
  const { tokens } = useV2Theme();
  const [formData, setFormData] = useState({
    email: email || "",
    newPassword: "",
    retypePassword: "",
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [gdprConsent, setGdprConsent] = useState(false);
  const [requiresConsent, setRequiresConsent] = useState(isInvitation);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const linkBtn = "text-sm font-medium text-blue-700 hover:text-blue-600";
  const hasValidLink = Boolean(email && token);
  const isInviteFlow = requiresConsent || isInvitation;

  useEffect(() => {
    if (email) {
      setFormData((prev) => ({ ...prev, email }));
    }
  }, [email]);

  useEffect(() => {
    if (!email || !token) return undefined;

    let cancelled = false;
    const loadContext = async () => {
      try {
        const params = new URLSearchParams({ email, token });
        const response = await apiFetch(`/api/auth/reset-password/context?${params}`);
        if (!response.ok || cancelled) return;
        const data = await response.json().catch(() => ({}));
        if (cancelled) return;
        if (data.requires_consent || data.is_invitation) {
          setRequiresConsent(true);
        }
      } catch {
        /* keep URL-derived invitation flag */
      }
    };

    loadContext();
    return () => {
      cancelled = true;
    };
  }, [email, token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError("");
  };

  const validatePassword = (password) => {
    if (password.length < 8) {
      return "Password must be at least 8 characters long";
    }
    if (password.length > 72) {
      return "Password must be no more than 72 characters long";
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setMessage("");

    if (formData.newPassword !== formData.retypePassword) {
      setError("Passwords do not match");
      setIsLoading(false);
      return;
    }

    const passwordError = validatePassword(formData.newPassword);
    if (passwordError) {
      setError(passwordError);
      setIsLoading(false);
      return;
    }

    if (isInviteFlow) {
      if (!termsAccepted) {
        setError("You must accept the Terms and Conditions to activate your account");
        setIsLoading(false);
        return;
      }
      if (!gdprConsent) {
        setError("You must provide GDPR consent to activate your account");
        setIsLoading(false);
        return;
      }
    }

    try {
      const payload = {
        email: formData.email,
        token,
        new_password: formData.newPassword,
        retype_password: formData.retypePassword,
      };
      if (isInviteFlow) {
        payload.terms_accepted = termsAccepted;
        payload.gdpr_consent = gdprConsent;
      }

      const response = await apiFetch("/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        const successMessage =
          data.message || "Password updated successfully. Redirecting to sign in...";
        setMessage(successMessage);
        setTimeout(() => {
          onSuccess?.();
        }, 3000);
      } else {
        const responseText = await response.text();
        setError(parseResetError(response, responseText));
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!hasValidLink) {
    return (
      <V2AuthLayout
        title={isInviteFlow ? "Invalid invitation link" : "Invalid reset link"}
        subtitle={
          isInviteFlow
            ? "This invitation link is invalid or has expired."
            : "This password reset link is invalid or has expired."
        }
      >
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          {isInviteFlow
            ? "Ask your administrator to send a new invitation, or use Forgot password on the sign-in page."
            : "Request a new reset link from the sign-in page, or contact support if the problem continues."}
        </div>
        <button type="button" onClick={() => onSuccess?.()} className={`mt-6 w-full text-center ${linkBtn}`}>
          Back to sign in
        </button>
      </V2AuthLayout>
    );
  }

  return (
    <V2AuthLayout
      title={isInviteFlow ? "Set your password" : "Set a new password"}
      subtitle={
        isInviteFlow
          ? "Create a password and accept the terms below to activate your account."
          : "Choose a new password for your account."
      }
    >
      <form className="space-y-4" onSubmit={handleSubmit}>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-slate-700">
            Email
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            disabled
            className={inputDisabledClass}
            value={formData.email}
            readOnly
          />
        </div>

        <div>
          <label htmlFor="newPassword" className="block text-sm font-medium text-slate-700">
            New password
          </label>
          <input
            id="newPassword"
            name="newPassword"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            maxLength={72}
            className={inputClass}
            placeholder="At least 8 characters"
            value={formData.newPassword}
            onChange={handleChange}
          />
          <p className="mt-1 text-xs text-slate-500">8-72 characters</p>
        </div>

        <div>
          <label htmlFor="retypePassword" className="block text-sm font-medium text-slate-700">
            Confirm new password
          </label>
          <input
            id="retypePassword"
            name="retypePassword"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            maxLength={72}
            className={inputClass}
            placeholder="Re-enter your password"
            value={formData.retypePassword}
            onChange={handleChange}
          />
        </div>

        {isInviteFlow ? (
          <div className="space-y-3 pt-1">
            <label className="flex items-start gap-3 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-700 focus:ring-blue-500"
              />
              <span>
                I accept the{" "}
                <a
                  href="https://decisionpen.com/terms-and-conditions"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-700 underline hover:text-blue-600"
                >
                  Terms and Conditions
                </a>{" "}
                *
              </span>
            </label>
            <label className="flex items-start gap-3 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={gdprConsent}
                onChange={(e) => setGdprConsent(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-700 focus:ring-blue-500"
              />
              <span>
                I consent to processing as described in the{" "}
                <a
                  href="https://decisionpen.com/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-700 underline hover:text-blue-600"
                >
                  Privacy Policy
                </a>{" "}
                (GDPR) *
              </span>
            </label>
          </div>
        ) : null}

        {error ? (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            {error}
          </div>
        ) : null}

        {message ? (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
            {message}
          </div>
        ) : null}

        <LoadingButton
          type="submit"
          loading={isLoading}
          loadingLabel={isInviteFlow ? "Activating…" : "Updating..."}
          disabled={Boolean(message)}
          className={`w-full ${tokens.button.primary} disabled:opacity-50`}
        >
          {isInviteFlow ? "Activate account" : "Update password"}
        </LoadingButton>

        <button type="button" onClick={() => onSuccess?.()} className={`w-full text-center ${linkBtn}`}>
          Back to sign in
        </button>
      </form>
    </V2AuthLayout>
  );
}